import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPrice } from "@/lib/api";
import { toast } from "sonner";

export default function AdminRentals() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/admin/rentals").then((r) => setItems(r.data.items || []));
  }, []);
  useEffect(load, [load]);

  const setVisibility = async (id, vis) => {
    try {
      await api.post(`/admin/rentals/${id}/visibility?visibility=${vis}`);
      toast.success(vis === "public" ? "Now visible to public" : "Made admin-only");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const removeRental = async (id, title) => {
    if (!confirm(`Delete rental "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Rental deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = items.filter((r) => filter === "all" || (r.visibility || "admin_only") === filter);

  return (
    <div className="p-10" data-testid="admin-rentals">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Internal inventory</div>
          <h1 className="font-serif text-4xl">Rentals</h1>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Rental inventory is <strong>not public</strong> by default. Point Penta agents
            match rentals to enquiring clients privately. Toggle a rental to <em>Public</em>
            only when the landlord has authorised open marketing.
          </p>
        </div>
        <Link
          to="/admin/listings/new?listing_type=rent"
          data-testid="admin-rental-new-btn"
          className="px-5 py-3 bg-[#001588] text-white text-sm uppercase tracking-[0.2em] hover:bg-black transition-colors"
        >
          + New rental
        </Link>
      </div>

      <div className="flex gap-2 mb-5">
        {["all", "admin_only", "public"].map((k) => (
          <button
            key={k}
            data-testid={`rental-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
              filter === k ? "bg-[#001588] text-white border-[#001588]" : "bg-white text-gray-700 border-[#E5E7EB]"
            }`}
          >
            {k.replace("_", " ")}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="border border-[#E5E7EB] bg-white p-10 text-center text-gray-500">
          No rental data available. Add a rental via the button above.
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-[#E5E7EB] text-left uppercase text-xs tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Community</th>
                <th className="px-4 py-3">Beds</th>
                <th className="px-4 py-3">Rent (AED / yr)</th>
                <th className="px-4 py-3">Visibility</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`rental-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.title}</td>
                  <td className="px-4 py-3 text-gray-600">{r.community || "—"}</td>
                  <td className="px-4 py-3">{r.bedrooms || "—"}</td>
                  <td className="px-4 py-3">{r.price ? fmtPrice(r.price, r.currency) : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 border ${
                      (r.visibility || "admin_only") === "public"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : "bg-gray-50 border-gray-200 text-gray-700"
                    }`}>
                      {(r.visibility || "admin_only").replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-3">
                    {(r.visibility || "admin_only") === "public" ? (
                      <button
                        onClick={() => setVisibility(r.id, "admin_only")}
                        data-testid={`rental-hide-${r.id}`}
                        className="text-xs uppercase tracking-wider text-amber-700 hover:underline"
                      >Make admin-only</button>
                    ) : (
                      <button
                        onClick={() => setVisibility(r.id, "public")}
                        data-testid={`rental-publish-${r.id}`}
                        className="text-xs uppercase tracking-wider text-[#001588] hover:underline"
                      >Publish to public</button>
                    )}
                    <Link to={`/admin/listings/${r.id}/edit`} className="text-xs uppercase tracking-wider text-gray-600 hover:underline">Edit</Link>
                    <button
                      onClick={() => removeRental(r.id, r.title)}
                      data-testid={`rental-delete-${r.id}`}
                      className="text-xs uppercase tracking-wider text-red-600 hover:underline"
                    >Delete</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">No rentals in this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
